<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="agent-information content-wrapper">
      <div class="page-header">{{ $t('agentInformation') }}</div>
      <el-container>
        <el-main>
          <el-row class="agent-status">
            <el-tag :type="(agent.isEnable === 1) ? 'success' : 'danger'">{{ getAgentStatus(agent.isEnable) }}</el-tag>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('name') }}:</strong>
            </div>
            <div>
              {{ agent.name }}
            </div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('type') }}:</strong>
            </div>
            <div>
              {{ getAgentTypeName() }}
            </div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('market') }}:</strong>
            </div>
            <div class="market-list">
              <span class="market-name" v-for="market in enableMarketList" :key="market.id">
                <span>
                  {{ market.name }}
                </span>
              </span>
              <br>
              <span class="market-name disable" v-for="market in disableMarketList" :key="market.id">
                <span v-if="market.isEnable !== 1">[{{ $t('STATUS.DISABLE') }}]</span>
                <span>
                  {{ market.name }}
                </span>
              </span>
            </div>
            <div v-if="enableMarketList.length < 1 && disableMarketList.length < 1">-</div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('address') }}:</strong>
            </div>
            <div>
              {{ (agent.information.address !== '') ? agent.information.address : '-' }}
            </div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('country') }}:</strong>
            </div>
            <div>
              {{ (agent.information.country !== '') ? agent.information.country : '-' }}
            </div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('zipCode') }}:</strong>
            </div>
            <div>
              {{ (agent.information.zipCode !== '') ? agent.information.zipCode : '-' }}
            </div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('email') }}:</strong>
            </div>
            <div class="information-data">
              <span v-for="(email, index) in agent.information.emailList" :key="`email-${index}`" class="data-list">
                {{ email }}
              </span>
            </div>
            <div v-if="agent.information.emailList.length === 1 && agent.information.emailList[0] === ''">-</div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('tel') }}:</strong>
            </div>
            <div class="information-data">
              <span v-for="(phone, index) in agent.information.phoneList" :key="`phone-${index}`" class="data-list">
                {{ phone }}
              </span>
            </div>
            <div v-if="agent.information.phoneList.length === 1 && agent.information.phoneList[0] === ''">-</div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('fax') }}:</strong>
            </div>
            <div class="information-data">
              <span v-for="(fax, index) in agent.information.faxList" :key="`fax-${index}`" class="data-list">
                {{ fax }}
              </span>
            </div>
            <div v-if="agent.information.faxList.length === 1 && agent.information.faxList[0] === ''">-</div>
          </el-row>
          <el-row>
            <div>
              <strong>{{ $t('internalNote') }}:</strong>
            </div>
            <div>
              {{ (agent.internalNote !== '') ? agent.internalNote : '-' }}
            </div>
          </el-row>
          <el-row class="change-info-button">
            <el-button type="primary" v-on:click="onEditButtonClick()">{{ $t('edit') }}</el-button>
          </el-row>
        </el-main>
      </el-container>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Agent/ManageAgent/Information/Index.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import AgentApi from '@/api/agent-api'
import { getStatusName } from '@/helpers'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import { STATUS } from '@/configs/constant'
import { mapState } from 'vuex'
import { get } from 'lodash'

export default {
  name: 'AgentInfo',
  mixins: [AlertViewMixin],
  components: { ContentContainer },
  i18n: { messages: i18nMessages },
  created: async function () {
    if (this.activeAgentId === undefined || this.activeAgentId === '') {
      this.$router.push({
        name: 'AgentList'
      })
    } else {
      this.getAgentInfo(this.activeAgentId)
    }
  },
  computed: {
    ...mapState({
      activeAgentId: state => state.general.activeAgentId
    })
  },
  watch: {
    activeAgentId: function (val) {
      this.getAgentInfo(val)
    }
  },
  data () {
    return {
      STATUS,
      isLoading: false,
      agent: {
        name: '',
        information: {
          address: '',
          country: '',
          zipCode: '',
          emailList: [],
          phoneList: [],
          faxList: []
        },
        internalNote: '',
        isEnable: STATUS.ENABLE
      },
      marketList: [],
      agentTypeList: [],
      enableMarketList: [],
      disableMarketList: []
    }
  },
  methods: {
    getAgentStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    getAgentInfo: async function (agentId) {
      try {
        this.isLoading = true

        const response = await AgentApi.info(agentId)
        this.agent = _prepareAgentData(response.agent)
        this.marketList = response.marketList
        this.agentTypeList = response.agentTypeList
        this.enableMarketList = _groupMarketListByStatus(this.agent.id, response.marketList, STATUS.ENABLE)
        this.disableMarketList = _groupMarketListByStatus(this.agent.id, response.marketList, STATUS.DISABLE)

        this.agentTypeList.sort((prev, next) => {
          return (prev.name < next.name) ? -1 : 1
        })
      } catch (err) {
        this.alertError(err.message)
      }

      this.isLoading = false
    },
    getAgentTypeName: function () {
      const agentType = this.agentTypeList.find(item => item.id === this.agent.agentTypeId)

      return (agentType !== undefined) ? agentType.name : '-'
    },
    onEditButtonClick: function () {
      this.$router.push({
        name: 'EditAgentInfo',
        params: {
          agent: this.agent,
          agentTypeList: this.agentTypeList,
          marketList: this.marketList,
          selectedMarketIdList: _getSelectedMarketIdList(this.agent.id, this.marketList)
        }
      })
    }
  }
}

function _groupMarketListByStatus (agentId, marketList, status) {
  const filteredMarketList = marketList.filter((market) => {
    const agentIdList = get(market, 'agentIdList', [])
    const agent = agentIdList.find(id => id === agentId)

    if (agent !== undefined && market.isEnable === status) {
      return market
    }
  })

  return filteredMarketList
}

function _prepareAgentData (agent) {
  const defaultInfo = {
    address: '',
    country: '',
    zipCode: '',
    emailList: [''],
    phoneList: [''],
    faxList: ['']
  }
  const defaultAgent = {
    name: '',
    isEnable: STATUS.ENABLE,
    information: { },
    internalNote: ''
  }
  agent.information = Object.assign({}, defaultInfo, agent.information)
  const agentData = Object.assign({}, defaultAgent, agent)

  return agentData
}

function _getSelectedMarketIdList (agentId, marketList) {
  const selectedMarketIdList = []

  marketList.map((market) => {
    const agentIdList = get(market, 'agentIdList', [])
    const agent = agentIdList.find(id => id === agentId)

    if (agent !== undefined) {
      selectedMarketIdList.push(market.id)
    }
  })

  return selectedMarketIdList
}
</script>

<style scoped>
  .el-header {
    background-color: #f8f9fa;
    color: #2c3e50;
    text-align: center;
    line-height: 60px;
    font-weight: bold;
  }

  .el-row {
    font-size: 14px;
    padding: 10px 0;
    border-bottom: 1px #dadbdd solid;
  }

  .agent-status, .change-info-button {
    border: 0;
    text-align: center;
  }

  .el-container {
    width: 80%;
    margin: 0 auto;
  }
  br:first-child {
    display: none;
  }
  .market-list:empty:before {
    content: "-"
  }
  .market-name:not(:last-of-type):after {
    content: ", ";
  }
  .information-data:empty:before {
    content: "-"
  }
  .data-list:not(:last-child):after {
    content: ", ";
  }
  .market-name.disable {
    color: #d2d1d1;
  }
</style>
